import { readonly, ref, useContext } from '@nuxtjs/composition-api';

import { ComposableFunctionArgs } from '~/composables';
import { useUiNotification } from '~/composables/useUiNotification';
import { Logger } from '~/helpers/logger';
import useCart from '~/modules/checkout/composables/useCart';
import { placeOrderCommand } from '~/modules/checkout/composables/useMakeOrder/commands/placeOrderCommand';
import type { PlaceOrderOutput } from '~/modules/GraphQL/types';

import type { UseMakeOrderErrors, UseMakeOrderInterface } from './useMakeOrder';

/**
 * Allows making an order from a cart.
 *
 * See the {@link UseMakeOrderInterface} for a list of methods and values available in this composable.
 */
export function useMakeOrder(): UseMakeOrderInterface {
  const loading = ref(false);
  const error = ref<UseMakeOrderErrors>({ make: null });
  const { cart } = useCart();
  const context = useContext();
  const { send: sendNotification } = useUiNotification();

  const make = async (params?: ComposableFunctionArgs<{}>): Promise<PlaceOrderOutput | null> => {
    Logger.debug('useMakeOrder.make');
    let placedOrder = null;
    try {
      loading.value = true;
      const { order, errors } = await placeOrderCommand.execute(
        context,
        cart.value.id,
        params,
      );
      placedOrder = order;
      error.value.make = null;
      if (errors && errors?.length) {
        sendNotification({
          id: Symbol('make_order_error'),
          message: errors[0]?.message as string,
          type: 'danger',
          icon: 'info',
          persist: true,
        });
      }
    } catch (err) {
      error.value.make = err;
      Logger.error('useMakeOrder.make', err);
    } finally {
      loading.value = false;
    }

    return placedOrder;
  };

  return {
    make,
    error: readonly(error),
    loading: readonly(loading),
  };
}

export * from './useMakeOrder';
export default useMakeOrder;
